import { Pegasus } from 'corso-types';
import { ComponentProps } from 'react';
import { useParams } from 'react-router';
import Alert from '~/components/Alert';
import LinkButton from '~/components/button/LinkButton';
import Submit from '~/components/button/Submit';
import CardSection from '~/components/CardSection';
import Divide from '~/components/Divide';
import Form from '~/components/Form';
import Icon from '~/components/Icon';
import Page from '~/components/layout/page/Page';
import { actionFormKey, useActionResult, useLoaderData } from './orderEdit';

type OrderEditAction = ReturnType<
  typeof useLoaderData
>['orderEditActions'][number];

type ActionType = OrderEditAction['editType'];

type ContentProps = {
  title: string;
  id: string;
  description: string;
  icon: ComponentProps<typeof Icon>['icon'];
  buttonText: string;
  buttonVariant: ComponentProps<typeof LinkButton>['variant'];
  path:
    | 'address'
    | 'cancel'
    | 'remove-shipping-protection'
    | 'modify-quantity'
    | 'email'
    | 'shipping-line'
    | 'discount-code';
};

const orderEditActionContent = {
  UPDATE_SHIPPING_ADDRESS: {
    buttonVariant: 'outlined',
    title: Pegasus.OrderEditDisplayContent.UPDATE_SHIPPING_ADDRESS.name,
    description:
      Pegasus.OrderEditDisplayContent.UPDATE_SHIPPING_ADDRESS
        .customerDescription,
    icon: 'LocationDot' as const,
    buttonText: 'Update Address',
    path: 'address',
    id: 'update-shipping-address',
  },
  CANCEL_ORDER: {
    buttonVariant: 'outlined',
    path: 'cancel',
    icon: 'XMark' as const,
    title: Pegasus.OrderEditDisplayContent.CANCEL_ORDER.name,
    description:
      Pegasus.OrderEditDisplayContent.CANCEL_ORDER.customerDescription,
    buttonText: 'Cancel',
    id: 'cancel-order',
  },
  REMOVE_PACKAGE_PROTECTION: {
    buttonVariant: 'outlined',
    path: 'remove-shipping-protection',
    icon: 'ShieldXmark' as const,
    title: Pegasus.OrderEditDisplayContent.REMOVE_PACKAGE_PROTECTION.name,
    description:
      Pegasus.OrderEditDisplayContent.REMOVE_PACKAGE_PROTECTION
        .customerDescription,
    buttonText: 'Remove Shipping Protection',
    id: 'remove-shipping-protection',
  },
  MODIFY_QUANTITY: {
    buttonVariant: 'outlined',
    path: 'modify-quantity',
    icon: 'CubesStacked' as const,
    title: Pegasus.OrderEditDisplayContent.MODIFY_QUANTITY.name,
    description:
      Pegasus.OrderEditDisplayContent.MODIFY_QUANTITY.customerDescription,
    buttonText: 'Adjust Product Quantity',
    id: 'modify-quantity',
  },
  UPDATE_EMAIL: {
    buttonVariant: 'outlined',
    path: 'email',
    icon: 'Envelope' as const,
    title: Pegasus.OrderEditDisplayContent.UPDATE_EMAIL.name,
    description:
      Pegasus.OrderEditDisplayContent.UPDATE_EMAIL.customerDescription,
    buttonText: 'Update Email',
    id: 'update-email',
  },
  UPDATE_SHIPPING_LINE: {
    buttonVariant: 'outlined',
    path: 'shipping-line',
    icon: 'Truck' as const,
    title: Pegasus.OrderEditDisplayContent.UPDATE_SHIPPING_LINE.name,
    description:
      Pegasus.OrderEditDisplayContent.UPDATE_SHIPPING_LINE.customerDescription,
    buttonText: 'Change Shipping Method',
    id: 'update-shipping-line',
  },
  ADD_DISCOUNT_CODE: {
    buttonVariant: 'outlined',
    path: 'discount-code',
    icon: 'DollarSign' as const,
    title: Pegasus.OrderEditDisplayContent.ADD_DISCOUNT_CODE.name,
    description:
      Pegasus.OrderEditDisplayContent.ADD_DISCOUNT_CODE.customerDescription,
    buttonText: 'Apply Discount Code',
    id: 'add-discount-code',
  },
} satisfies Record<ActionType, ContentProps>;

function EditItem({ action }: { action: OrderEditAction }) {
  const content = orderEditActionContent[action.editType];

  return (
    <CardSection
      icon={content.icon}
      title={content.title}
      description={content.description}
      action={
        <Form className="items-stretch" id={content.id}>
          <input
            type="hidden"
            name={actionFormKey}
            value={JSON.stringify(action)}
          />
          <Submit
            className="md:w-full"
            variant={content.buttonVariant}
            form={content.id}
          >
            {content.buttonText}
          </Submit>
        </Form>
      }
    />
  );
}

// ? maybe useSubmit and have the edit item have an onClick?
export default function EditOrderPage() {
  const { orderEditActions, orderEditExpiredDescriptionText } = useLoaderData();
  const actionResult = useActionResult();
  const params = useParams();

  const hasActions = orderEditActions.length > 0;

  return (
    <Page.Root title="Edit Your Order" hideHeader>
      {actionResult?.ok === false && (
        <Alert variant="error" title={actionResult.message} />
      )}
      {hasActions ? (
        <Divide as="ul" ariaLabel="Possible Edit Actions">
          {orderEditActions.map((action) => (
            <li key={action.editType}>
              <EditItem action={action} />
            </li>
          ))}
        </Divide>
      ) : (
        <>
          <Alert title="Order Editing Unavailable" variant="info">
            {orderEditExpiredDescriptionText ??
              'This order is no longer able to be edited, please contact us for support.'}
          </Alert>
          <LinkButton
            relative="path"
            to={`/${params.store}/order/${params.idFromPlatform}`}
            className="w-full shrink-0 md:w-60"
          >
            Go Home
          </LinkButton>
        </>
      )}
    </Page.Root>
  );
}
